/**
 * The single letter drawn on a recipe's placeholder when it has no usable photo.
 *
 * It is the first letter of the *dish*, not of the string: French recipe titles
 * lead with an article far more often than English ones, and a browse grid where
 * a third of the tiles say "L" carries no information at all.
 *
 * Articles are only stripped when they stand as a word (`Le poulet` → `P`) or
 * elide onto the noun (`L'agneau` → `A`). A bare prefix match would turn
 * `Lasagnes` into `S`, which is why the trailing `\s+` / apostrophe is not
 * optional.
 */

/**
 * `le|la|les|un|une|des|du|de|the|a|an` as a word, or `l'`/`d'` elided.
 *
 * Both apostrophes are listed: scraped French titles carry the typographic
 * `’` at least as often as the straight `'`.
 */
const LEADING_ARTICLE = /^(?:(?:les?|la|une?|des|du|de|the|an?)\s+|[ld][’'])/i;

/** Leading punctuation, quotes, emoji — anything before the first real character. */
const LEADING_NOISE = /^[^\p{L}\p{N}]+/u;

/**
 * Returns one uppercase character, or `''` when the title has no letter or digit
 * to take one from. Callers render the wash alone in that case rather than a
 * `?`, which would read as an error state.
 */
export function recipeInitial(title?: string | null): string {
  const cleaned = (title ?? '').trim().replace(LEADING_NOISE, '');
  if (!cleaned) return '';

  // Keep the article when removing it would leave nothing (a title that *is*
  // the article, e.g. "Le").
  const withoutArticle = cleaned.replace(LEADING_ARTICLE, '').replace(LEADING_NOISE, '');
  const source = withoutArticle || cleaned;

  return [...source][0].toLocaleUpperCase('fr');
}

export default recipeInitial;
